import * as React from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { composeRefs } from '../../internals/composeRefs';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { useControllable } from '../../internals/useControllable';

import {
  CalendarTrigger,
  ControlShell,
  DatePickerCalendarGlobalStyles,
  StyledInput,
  StyledRoot,
} from './DatePicker.styles';

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const DATE_FORMAT_ALIASES: Record<string, string> = {
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'yyyy-MM-dd': 'yyyy-MM-dd',
  'MM/dd/yyyy h:mm aa': 'MM/dd/yyyy h:mm a',
};

export function mapDateFnsFormat(pattern: string): string {
  return DATE_FORMAT_ALIASES[pattern] ?? pattern;
}

/** `production` = customer `datePicker.styled.ts` scale (root `rem`). `relaxed` = slightly larger. */
export type DatePickerCalendarDensity = 'production' | 'relaxed';

export type DatePickerCalendarIcon = 'calendar_month' | 'access_time';

export type DatePickerRenderCustomHeader = NonNullable<
  React.ComponentProps<typeof ReactDatePicker>['renderCustomHeader']
>;

export interface DatePickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  format?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  invalid?: boolean;
  readOnly?: boolean;
  semanticId?: string;
  /** Appends to the outer root (e.g. `customDatepicker-active` when open). */
  rootClassName?: string;
  /** Merged onto the text input (e.g. `date-field is-invalid`). */
  inputClassName?: string;
  /** Default **production** (matches app `datePicker.styled.ts`). Use **relaxed** for a slightly larger panel. */
  calendarDensity?: DatePickerCalendarDensity;
  /** When true, uses `position: fixed` for the popper (overflow contexts). */
  fixOverflow?: boolean;
  /** When true, renders the calendar popup into a body-level portal (use inside overflow:hidden containers like table cells). */
  portalToBody?: boolean;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  timeIntervals?: number;
  timeCaption?: string;
  showMonthYearPicker?: boolean;
  calendarIcon?: DatePickerCalendarIcon;
  renderCustomHeader?: DatePickerRenderCustomHeader;
  onCalendarOpen?: () => void;
  onCalendarClose?: () => void;
  onClickOutside?: (event: MouseEvent) => void;
  /** Passed to react-datepicker (input blur). */
  onInputBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Spread onto the text input (e.g. `onKeyUp`). */
  inputProps?: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>;
  name?: string;
}

type CustomInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  disabled?: boolean;
  readOnly?: boolean;
  invalid?: boolean;
  calendarIcon?: DatePickerCalendarIcon;
  inputProps?: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>;
};

const DatePickerCustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  function DatePickerCustomInput(
    {
      disabled,
      readOnly,
      invalid,
      calendarIcon = 'calendar_month',
      inputProps,
      onClick,
      ...rest
    },
    ref,
  ) {
    const openPicker: React.MouseEventHandler<HTMLElement> = (e) => {
      onClick?.(e as unknown as React.MouseEvent<HTMLInputElement>);
    };
    return (
      <ControlShell>
        <StyledInput
          {...inputProps}
          {...rest}
          ref={ref}
          readOnly={readOnly}
          $disabled={!!disabled}
          $invalid={!!invalid}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          onClick={openPicker}
        />
        <CalendarTrigger
          type="button"
          disabled={disabled}
          aria-label="Open calendar"
          tabIndex={-1}
          onClick={(e) => {
            e.preventDefault();
            openPicker(e);
          }}
        >
          <span className="material-symbols-outlined" aria-hidden>
            {calendarIcon}
          </span>
        </CalendarTrigger>
      </ControlShell>
    );
  },
);

DatePickerCustomInput.displayName = 'DatePickerCustomInput';

export const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(function DatePicker(
  {
    value: valueProp,
    onChange,
    placeholder = 'Select date',
    format = 'MM/dd/yyyy',
    minDate,
    maxDate,
    disabled = false,
    invalid = false,
    readOnly = true,
    semanticId,
    rootClassName,
    inputClassName,
    calendarDensity = 'production',
    fixOverflow = true,
    portalToBody = false,
    showTimeSelect = false,
    showTimeSelectOnly = false,
    timeIntervals = 15,
    timeCaption = 'Time',
    showMonthYearPicker = false,
    calendarIcon = 'calendar_month',
    renderCustomHeader,
    onCalendarOpen,
    onCalendarClose,
    onClickOutside,
    onInputBlur,
    inputProps,
    name,
    ...rest
  },
  ref,
) {
  const { id: rootIdProp, className: rootClassFromRest, style, ...rootAttrs } = rest;
  const generatedId = React.useId().replace(/:/g, '');
  const localPortalId = rootIdProp ?? `wl-datepicker-${generatedId}`;

  const bodyPortalId = `wl-datepicker-body-${generatedId}`;
  React.useEffect(() => {
    if (!portalToBody) return;
    let el = document.getElementById(bodyPortalId);
    if (!el) {
      el = document.createElement('div');
      el.id = bodyPortalId;
      el.className = 'wl-datepicker-ds';
      el.setAttribute('data-calendar-density', calendarDensity);
      document.body.appendChild(el);
    }
    return () => {
      el?.remove();
    };
  }, [portalToBody, bodyPortalId, calendarDensity]);

  const portalId = portalToBody ? bodyPortalId : localPortalId;

  const [value, setValue] = useControllable<Date | null>({
    value: valueProp,
    defaultValue: null,
    onChange,
  });
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const semanticProps = getSemanticAttributes(semanticId);
  const composedRef = composeRefs(ref, rootRef);
  const [calendarOpen, setCalendarOpen] = React.useState(false);

  const handleChange = (d: Date | null) => {
    if (!d) {
      setValue(null);
      return;
    }
    setValue(showTimeSelect ? d : startOfDay(d));
  };

  const mergedRootClass = [
    'wl-datepicker-ds',
    'customDatepicker',
    calendarOpen ? 'customDatepicker-active' : '',
    rootClassName,
    rootClassFromRest,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <StyledRoot
      ref={composedRef}
      id={localPortalId}
      className={mergedRootClass}
      style={style}
      data-state={calendarOpen ? 'open' : 'closed'}
      data-calendar-density={calendarDensity}
      {...semanticProps}
      {...rootAttrs}
    >
      <DatePickerCalendarGlobalStyles />
      <ReactDatePicker
        portalId={portalId}
        selected={value}
        onChange={handleChange}
        dateFormat={mapDateFnsFormat(format)}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        placeholderText={placeholder}
        strictParsing
        showPopperArrow={false}
        popperProps={fixOverflow ? { strategy: 'fixed' } : undefined}
        wrapperClassName="wl-datepicker-ds-react-datepicker-wrapper"
        name={name}
        customInput={
          <DatePickerCustomInput
            disabled={disabled}
            readOnly={readOnly}
            invalid={invalid}
            calendarIcon={calendarIcon}
            inputProps={inputProps}
            placeholder={placeholder}
            className={inputClassName}
          />
        }
        onCalendarOpen={() => {
          setCalendarOpen(true);
          onCalendarOpen?.();
        }}
        onCalendarClose={() => {
          setCalendarOpen(false);
          onCalendarClose?.();
        }}
        onClickOutside={onClickOutside}
        onBlur={onInputBlur}
        autoComplete="off"
        showTimeSelect={showTimeSelect}
        showTimeSelectOnly={showTimeSelectOnly}
        timeIntervals={timeIntervals}
        timeCaption={timeCaption}
        showMonthYearPicker={showMonthYearPicker}
        renderCustomHeader={renderCustomHeader}
      />
    </StyledRoot>
  );
});

DatePicker.displayName = 'DatePicker';
